/**
 * Class representing a damage claim
 */
import {User} from './user'
import {Flight} from './flight'
import {Address} from './address'
import {BaggageItem} from './baggageItem'
import {ContentItem} from './contentItem'
import {Language} from './language'

/**
 * Each claim must be of one of the following types chosen by the user at the start
 * Note, theese should stay in sync with claim-types.json and hence server side
 */
export const CLAIM_TYPES = {
  MISSING: 1,
  DAMAGED: 2,
  EXPENSE: 3
}

export class DamageClaim {
  /**
   * Create a DamageClaim.
   * @param {object} options - the damage claim options
   * @param {Language} options.language - the language object
   * @param {User} options.user - the user object
   * @param {Address} options.address - the address object
   * @param {Flight} options.flight - the flight object
   * @param {[BaggageItem]} options.baggageItems - an array of baggage items
   * @param {[ContentItem]} options.contentItems - an array of content items
   * @param {boolean} options.dataProtectionConsent - boolean presenting
   *                                      if the user agrred with the conditions
   * @param {boolean} options.isReported - the boolean representing if the accident was reported
   *                                        in the airport
   * @param {String} options.pirReportReference - the string of the PIR
   * @param {number} options.dateReported - Epoch of the reported date
   * @param {String} options.additionalInfo - string for the claim additional info
   * @param {String} options.buildId - string for the build id - string admin can guess which machine is submitting
   * @param {boolean} options.termsAgreed - t&c's agreed
   * @param {boolean} options.infoConfirmed - user agreed all info correct
   * @param {number} options.claimType - user agreed all info correct
   * @param {boolean} options.isPirPulledFromAirportReport - true if claim loaded from airport report using PIR
   * @param {boolean} options.boardingPassConfirmed - true if claim loaded from airport boarding pass
   * @param {boolean} options.boardingPassRaw - raw string of boarding pass
   * @param {boolean} options.isKiosk - if true then claim is created using kiosk app
   * @param {boolean} options.plannedTravel - if true then customer is planning to travel soon
   * @param {boolean} options.plannedTravelDate - date when customer is planning to travel
   */
  constructor (options) {
    if (options) {
      this.language = options.language
      this.user = options.user
      this.address = options.address
      this.tempAddress = options.tempAddress
      this.hasTempAddress = options.hasTempAddress
      this.dateAtTempAddress = options.dateAtTempAddress
      this.plannedTravel = options.plannedTravel
      this.plannedTravelDate = options.plannedTravelDate
      this.flight = options.flight
      this.baggageItems = options.baggageItems
      this.contentItems = options.contentItems
      this.dataProtectionConsent = options.dataProtectionConsent
      this.isReported = options.isReported
      this.pirReportReference = options.pirReportReference
      this.dateReported = options.dateReported
      this.additionalInfo = options.additionalInfo
      this.buildId = options.buildId
      this.termsAgreed = options.termsAgreed
      this.infoConfirmed = options.infoConfirmed
      this.claimProviderId = options.claimProviderId
      this.airlineCode = options.airlineCode
      this.claimType = options.claimType
      this.isPirPulledFromAirportReport = options.isPirPulledFromAirportReport
      this.isKiosk = options.isKiosk
      this.boardingPassConfirmed = options.boardingPassConfirmed
      this.boardingPassRaw = options.boardingPassRaw
    }
  }

  get user () { return this._user }
  set user (user) {
    if (user && !(user instanceof User)) {
      throw new TypeError('user is not an instance of User')
    }
    this._user = user
  }

  get address () { return this._address }
  set address (address) {
    if (address && !(address instanceof Address)) {
      throw new TypeError('address is not an instance of Address')
    }
    this._address = address
  }

  get tempAddress () { return this._tempAddress }
  set tempAddress (address) {
    if (address && !(address instanceof Address)) {
      throw new TypeError('address is not an instance of Address')
    }
    this._tempAddress = address
  }

  get hasTempAddress () { return this._hasTempAddress }
  set hasTempAddress (hasTempAddress) {
    this._hasTempAddress = hasTempAddress
  }

  get dateAtTempAddress () { return this._dateAtTempAddress }
  set dateAtTempAddress (dateAtTempAddress) {
    if (dateAtTempAddress) this._dateAtTempAddress = Number(dateAtTempAddress)
    else this._dateAtTempAddress = null
  }

  get plannedTravel () { return this._plannedTravel }
  set plannedTravel (plannedTravel) {
    this._plannedTravel = plannedTravel
  }

  get plannedTravelDate () { return this._plannedTravelDate }
  set plannedTravelDate (plannedTravelDate) {
    if (plannedTravelDate) this._plannedTravelDate = Number(plannedTravelDate)
    else this._plannedTravelDate = null
  }

  get flight () { return this._flight }
  set flight (flight) {
    if (flight && !(flight instanceof Flight)) {
      throw new TypeError('flight is not an instance of Flight')
    }
    this._flight = flight
  }

  get baggageItems () { return this._baggageItem }
  set baggageItems (baggageItems) {
    baggageItems.forEach(item => {
      if (item && !(item instanceof BaggageItem)) {
        throw new TypeError('baggageItem is not an instance of BaggageItem')
      }
    })
    this._baggageItems = baggageItems
  }

  get contentItems () { return this._contentItems }
  set contentItems (contentItems) {
    contentItems.forEach(item => {
      if (item && !(item instanceof ContentItem)) {
        throw new TypeError('contentItem is not an instance of ContentItem')
      }
    })
    this._contentItems = contentItems
  }

  get language () { return this._language }
  set language (language) {
    if (language && !(language instanceof Language)) {
      throw new TypeError('language is not an instance of Language')
    }
    this._language = language
  }

  get dataProtectionConsent () { return this._dataProtectionConsent }
  set dataProtectionConsent (dataProtectionConsent) {
    this._dataProtectionConsent = dataProtectionConsent
  }

  get infoConfirmed () { return this._infoConfirmed }
  set infoConfirmed (value) {
    this._infoConfirmed = value
  }

  get termsAgreed () { return this._termsAgreed }
  set termsAgreed (value) {
    this._termsAgreed = value
  }

  get isReported () { return this._isReported }
  set isReported (isReported) {
    this._isReported = isReported
  }

  get pirReportReference () { return this._pirReportReference }
  set pirReportReference (pirReportReference) {
    if (pirReportReference) this._pirReportReference = String(pirReportReference)
    else this._pirReportReference = null
  }

  get dateReported () { return this._dateReported }
  set dateReported (dateReported) {
    if (dateReported) this._dateReported = Number(dateReported)
    else this._dateReported = null
  }

  get additionalInfo () { return this._additionalInfo }
  set additionalInfo (additionalInfo) {
    if (additionalInfo) this._additionalInfo = String(additionalInfo)
    else this._additionalInfo = null
  }

  get buildId () { return this._buildId }
  set buildId (buildId) {
    if (buildId) this._buildId = String(buildId)
    else this._buildId = null
  }

  get claimProviderId () { return this._claimProviderId }
  set claimProviderId (claimProviderId) {
    if (claimProviderId) this._claimProviderId = Number(claimProviderId)
    else this._claimProviderId = null
  }

  get airlineCode () { return this._airlineCode }
  set airlineCode (airlineCode) {
    if (airlineCode) this._airlineCode = String(airlineCode)
    else this._airlineCode = null
  }

  get claimType () { return this._claimType }
  set claimType (claimType) {
    if (claimType) this._claimType = Number(claimType)
    else this._claimType = null
  }

  get isPirPulledFromAirportReport () { return this._isPirPulledFromAirportReport }
  set isPirPulledFromAirportReport (isPirPulledFromAirportReport) {
    this._isPirPulledFromAirportReport = isPirPulledFromAirportReport
  }

  get isKiosk () { return this._isKiosk }
  set isKiosk (isKiosk) {
    this._isKiosk = isKiosk
  }

  get boardingPassConfirmed () { return this._boardingPassConfirmed }
  set boardingPassConfirmed (boardingPassConfirmed) {
    this._boardingPassConfirmed = boardingPassConfirmed
  }

  get boardingPassRaw () { return this._boardingPassRaw }
  set boardingPassRaw (boardingPassRaw) {
    if (boardingPassRaw) this._boardingPassRaw = String(boardingPassRaw)
    else this._boardingPassRaw = null
  }

  toJSON () {
    return {
      language: this._language,
      user: this._user,
      address: this._address,
      luggageAddress: this._hasTempAddress ? this._tempAddress : null,
      luggageAddressIsHomeAddress: !this._hasTempAddress,
      dateAtLuggageAddress: this._dateAtTempAddress,
      plannedTravel: this._plannedTravel,
      plannedTravelDate: this._plannedTravel ? this._plannedTravelDate : null,
      flight: this._flight,
      baggageItems: this._baggageItems,
      contentItems: this._contentItems,
      dataProtectionConsent: this._dataProtectionConsent,
      isReported: this._isReported,
      pirReportReference: this._pirReportReference,
      dateReported: this._dateReported,
      additionalInfo: this._additionalInfo,
      buildId: this._buildId,
      claimProviderId: this._claimProviderId,
      airlineCode: this._airlineCode,
      claimType: this._claimType,
      isPirPulledFromAirportReport: this._isPirPulledFromAirportReport,
      isKiosk: this.isKiosk,
      boardingPassConfirmed: this.boardingPassConfirmed,
      boardingPassRaw: this.boardingPassRaw
    }
  }
}
